import createProps from '@kissui/helpers/src/props.helpers'
import { EVENT_QS_OPEN, EVENT_ADD_TO_CART, EVENT_QS_CLOSED } from '@kissui/components'
import { QUANTITY_SELECTOR_ID } from './components/qs/constants'
import { dispatchEvent, readEvent } from '@kissui/helpers/src/assets/js/eventDispatch'
import { getProduct } from '@kissui/helpers/src/catalog'
import './components/qs'

class AddToCartStandalone extends HTMLElement {
    constructor() {
        super()
        this.props = {}
        this.boundOnAddToCart = this.onAddToCart.bind(this)
        this.boundOnCartUpdated = this.onCartUpdated.bind(this)
        this.boundOnQSClosed = this.onQSClosed.bind(this)
        this.qsId = null
        this.inStock = true
    }

    connectedCallback() {
        try {
            this.props = createProps(this.attributes)
        } catch (e) {
            console.error(`AddToCartStandalone: data attribute isn't formatted properly`)
        }

        const { sku } = this.props
        this.qsId = `${QUANTITY_SELECTOR_ID}-${sku.replace(/[^\w\s]/gi, '-')}`
        this.render()

        this.onPostRender().then(() => {
            this.onRenderReady()
        })
    }

    render() {
        const { size = 'large', label_add_to_cart, label_update_cart } = this.props
        const { __INITIAL_CONFIG__: { translations = {} } = {} } = window

        const btnText = translations['global.addtobag.button.add'] || label_add_to_cart
        const btnUpdate = translations['global.addtobag.button.update'] || label_update_cart

        this.classList.add('nb-addToCart')
        this.innerHTML = `
            <nb-cta
                variation="primary"
                icon_right="24/symbol/plus"
                add_to_cart="${size}"
                label_update="${btnUpdate}"
                qs_id="${this.qsId}">
                ${btnText}
            </nb-cta>
        `

        this.buttonElement = this.querySelector('nb-cta')
    }

    async onPostRender() {
        this.inStock = await Promise.resolve(this.props.in_stock ?? this.checkStock())
        this.readCurrentCart()
        if (!this.inStock) {
            this.querySelector('nb-cta').setAttribute('disabled', '')
        }
    }

    async checkStock() {
        if (!window.napi) {
            return
        }
        const { inStock } = await getProduct(this.props.sku)
        return inStock
    }

    readCurrentCart() {
        if (!window.napi) {
            return
        }

        const { sku } = this.props

        window.napi
            .cart()
            .read()
            .then(products => {
                const productMatch = products.find(product => product.productId.indexOf(sku) > -1)
                this.updateQuantity(productMatch?.quantity ?? 0)
            })
    }

    performAddToCart(quantity) {
        const { sku } = this.props
        return window.CartManager.updateItem(sku, quantity, null, null)
    }

    async onAddToCart(e) {
        if (!window.CartManager) {
            return
        }

        let { quantity } = readEvent(e)
        quantity = parseInt(quantity, 10)

        // TODO: check online add, remove, change quantity
        try {
            await this.performAddToCart(quantity)
            this.updateQuantity(quantity)
        } catch (e) {
            console.error(e)
        }
    }

    updateQuantity(value) {
        if (value === parseInt(this.buttonElement.getAttribute('quantity'), 10)) {
            return
        }
        this.buttonElement.setAttribute('quantity', value)
    }

    onCartUpdated() {
        this.readCurrentCart()
    }

    onQSClosed() {
        this.removeEventListener(EVENT_ADD_TO_CART, this.boundOnAddToCart)
        if (this.qsElement) {
            this.qsElement.removeEventListener(EVENT_QS_CLOSED, this.boundOnQSClosed)
        }
    }

    openQuantitySelector() {
        this.renderQuantitySelector()
        if (!this.contains(this.qsElement)) {
            // Change the DOM position only if we are opening a QS in another button
            this.qsElement.close()
            this.appendChild(this.qsElement)
        }
        this.addEventListener(EVENT_ADD_TO_CART, this.boundOnAddToCart)
        this.qsElement.addEventListener(EVENT_QS_CLOSED, this.boundOnQSClosed)
        dispatchEvent({
            eventName: EVENT_QS_OPEN,
            args: { id: this.qsId }
        })
    }

    renderQuantitySelector() {
        this.qsElement = this.querySelector(`#${this.qsId}`)
        if (this.qsElement !== null) {
            return
        }

        const { quantity_selector } = this.props
        this.qsElement = document.createElement('nb-qs')
        const data = {
            id: this.qsId,
            a11y_qs_label: quantity_selector.a11y_qs_label,
            a11y_qs_description: quantity_selector.a11y_qs_description,
            a11y_predefined_quantity: quantity_selector.a11y_predefined_quantity,
            a11y_predefined_description: quantity_selector.a11y_predefined_description,
            a11y_add_label: quantity_selector.a11y_add_label,
            quantities: quantity_selector.quantities,
            step: quantity_selector.step,
            max: quantity_selector.max,
            a11y_custom_quantity: quantity_selector.a11y_custom_quantity,
            a11y_custom_description: quantity_selector.a11y_custom_description,
            label_choose_quantity: quantity_selector.label_choose_quantity,
            label_button_ok: quantity_selector.label_button_ok
        }
        this.qsElement.setAttribute('data', JSON.stringify(data))
    }

    onRenderReady() {
        this.bindEvent()
    }

    bindUpdateCart() {
        if (window.napi && window.napi.data) {
            try {
                window.napi.data().on('cart.*', this.boundOnCartUpdated)
            } catch (e) {
                console.error(e)
            }
        }
    }

    bindEvent() {
        this.unbindEvent()
        this.bindUpdateCart()

        if (this.buttonElement && this.inStock) {
            this.boundQuantitySelectorClick = this.openQuantitySelector.bind(this)
            this.buttonElement.addEventListener('click', this.boundQuantitySelectorClick)
        }
    }

    unbindEvent() {
        this.removeEventListener(EVENT_ADD_TO_CART, this.boundOnAddToCart)

        if (this.buttonElement && this.inStock) {
            this.buttonElement.removeEventListener('click', this.boundQuantitySelectorClick)
        }

        if (this.qsElement) {
            this.qsElement.removeEventListener(EVENT_QS_CLOSED, this.boundOnQSClosed)
        }

        if (window.napi && window.napi.data) {
            window.napi.data().off('cart.*', this.boundOnCartUpdated)
        }
    }

    disconnectedCallback() {
        this.unbindEvent()
    }
}

customElements.get('nb-add-to-cart-standalone') ||
    customElements.define('nb-add-to-cart-standalone', AddToCartStandalone)

export default AddToCartStandalone
